import React, { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AdminLayout } from "@/pages/admin/AdminLayout";
import { toast } from "sonner";
import { Upload, Sparkles, CheckCircle2, RotateCcw, FileSpreadsheet } from "lucide-react";

const FIELD_LABELS = {
  name: "Name", first_name: "First name", last_name: "Last name", email: "Email", phone: "Phone",
  country: "Country", language: "Language", lifecycle_stage: "Lifecycle stage", source: "Source",
  campaign: "Campaign", tags: "Tags", programme_interest: "Programme interest",
  consent: "Marketing consent", unsubscribed: "Unsubscribed", notes: "Notes", ignore: "— Ignore —",
};

export default function AdminImports() {
  const [analysis, setAnalysis] = useState(null);
  const [mapping, setMapping] = useState({});
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const [extraTags, setExtraTags] = useState("");
  const [forceConsent, setForceConsent] = useState(false);
  const [defaultStage, setDefaultStage] = useState("lead");
  const fileRef = useRef();

  const loadHistory = () => api.get("/admin/imports").then((r) => setHistory(r.data));
  useEffect(() => { loadHistory(); }, []);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true); setReport(null);
    const fd = new FormData(); fd.append("file", f);
    try {
      const r = await api.post("/admin/imports/analyze", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAnalysis(r.data); setMapping(r.data.mapping);
      toast.success(r.data.ai_used ? "Simo AI proposed a mapping" : "Columns mapped (heuristics)");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setBusy(false); if (fileRef.current) fileRef.current.value = "";
  };

  const commit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/imports/${analysis.import_id}/commit`, {
        mapping, default_lifecycle: defaultStage, default_source: "kajabi", merge: true,
        extra_tags: extraTags.split(",").map((t) => t.trim()).filter(Boolean),
        force_consent: forceConsent });
      setReport(r.data); setAnalysis(null); setExtraTags(""); setForceConsent(false); loadHistory();
      toast.success(`Imported: ${r.data.created} new, ${r.data.updated} updated`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setBusy(false);
  };

  const rollback = async (iid) => {
    if (!window.confirm("Roll back this import? Contacts created by it will be removed.")) return;
    const r = await api.post(`/admin/imports/${iid}/rollback`); toast.success(`Removed ${r.data.removed} contacts`); loadHistory();
  };

  return (
    <AdminLayout>
      <div className="p-8 max-w-6xl" data-testid="admin-imports-page">
        <h1 className="font-serif text-3xl">Kajabi Import</h1>
        <p className="text-paper/50 text-sm mt-1">Upload a Kajabi contact export. Simo AI reads your columns and proposes a mapping — preview, then import with duplicate detection.</p>

        {!analysis && (
          <label className="mt-6 flex flex-col items-center justify-center gap-3 border-2 border-dashed border-white/15 rounded-lg p-12 cursor-pointer hover:border-heraldic transition-colors" data-testid="import-dropzone">
            <FileSpreadsheet className="h-10 w-10 text-heraldic" />
            <div className="text-sm text-paper/70">{busy ? "Analysing…" : "Click to upload a CSV export"}</div>
            <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} data-testid="import-file-input" />
          </label>
        )}

        {report && (
          <div className="mt-6 border border-emerald-500/30 bg-emerald-500/10 rounded-md p-5 flex items-center gap-4" data-testid="import-report">
            <CheckCircle2 className="h-8 w-8 text-emerald-400" />
            <div className="text-sm"><b>{report.created}</b> created · <b>{report.updated}</b> updated · {report.skipped} skipped · {report.errors} errors</div>
          </div>
        )}

        {analysis && (
          <div className="mt-6" data-testid="import-mapping">
            <div className="flex items-center gap-2 text-sm text-paper/60 mb-3">
              {analysis.ai_used && <span className="inline-flex items-center gap-1 text-heraldic"><Sparkles className="h-4 w-4" /> AI-assisted</span>}
              <span>{analysis.total_rows} rows · {analysis.duplicate_estimate} likely duplicate(s)</span>
            </div>
            <div className="border border-white/10 rounded-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-white/5 text-paper/50 text-xs uppercase"><tr><th className="text-left px-4 py-2">CSV column</th><th className="text-left px-4 py-2">Sample</th><th className="text-left px-4 py-2">→ Simo field</th></tr></thead>
                <tbody>
                  {analysis.headers.map((h) => (
                    <tr key={h} className="border-t border-white/5">
                      <td className="px-4 py-2 font-medium">{h}</td>
                      <td className="px-4 py-2 text-paper/40">{String(analysis.sample?.[0]?.[h] || "").slice(0, 30)}</td>
                      <td className="px-4 py-2">
                        <select value={mapping[h] || "ignore"} onChange={(e) => setMapping({ ...mapping, [h]: e.target.value })} data-testid={`map-${h}`} className="bg-[#0B0D12] border border-white/10 rounded-sm px-2 py-1 text-sm">
                          {["ignore", ...analysis.fields].map((f) => <option key={f} value={f}>{FIELD_LABELS[f] || f}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap items-end gap-4 mt-4 border border-white/10 rounded-sm p-4 bg-white/5" data-testid="import-options">
              <label className="text-xs text-paper/60">Add tag(s) to everyone in this import
                <input value={extraTags} onChange={(e) => setExtraTags(e.target.value)} placeholder="Founding Cohort 2025-26, Africa" className="mt-1 block bg-[#0B0D12] border border-white/10 rounded-sm px-3 py-2 text-sm w-72" data-testid="import-extra-tags" /></label>
              <label className="text-xs text-paper/60">Default role
                <select value={defaultStage} onChange={(e) => setDefaultStage(e.target.value)} className="mt-1 block bg-[#0B0D12] border border-white/10 rounded-sm px-3 py-2 text-sm capitalize" data-testid="import-default-stage">
                  {["lead", "parent", "student", "applicant", "customer", "educator", "partner", "investor"].map((s) => <option key={s} value={s}>{s}</option>)}
                </select></label>
              <label className="flex items-center gap-2 text-sm text-paper/70 pb-2"><input type="checkbox" checked={forceConsent} onChange={(e) => setForceConsent(e.target.checked)} data-testid="import-force-consent" /> Mark all as marketing-consented</label>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={commit} disabled={busy} data-testid="import-commit" className="inline-flex items-center gap-1.5 bg-heraldic text-white px-4 py-2 rounded-sm text-sm disabled:opacity-50"><Upload className="h-4 w-4" /> Import {analysis.total_rows} contacts</button>
              <button onClick={() => setAnalysis(null)} className="border border-white/15 px-4 py-2 rounded-sm text-sm">Cancel</button>
            </div>
          </div>
        )}

        <div className="mt-10">
          <h2 className="text-lg font-serif mb-3">Import history</h2>
          <div className="space-y-2" data-testid="import-history">
            {history.length === 0 && <p className="text-paper/40 text-sm">No imports yet.</p>}
            {history.map((h) => (
              <div key={h.iid} className="flex items-center justify-between border border-white/10 rounded-sm px-4 py-3 bg-white/5">
                <div><div className="text-sm font-medium">{h.filename}</div><div className="text-xs text-paper/40">{h.created_at?.slice(0, 16).replace("T", " ")} · {h.status}{h.report && ` · ${h.report.created} new, ${h.report.updated} updated`}</div></div>
                {h.status === "committed" && <button onClick={() => rollback(h.iid)} data-testid={`rollback-${h.iid}`} className="inline-flex items-center gap-1.5 text-xs text-red-400 border border-red-400/30 px-2.5 py-1.5 rounded-sm"><RotateCcw className="h-3.5 w-3.5" /> Roll back</button>}
                {h.status === "rolled_back" && <span className="text-xs text-paper/40">rolled back</span>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
